import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type FrontendSuppressionEvent,
  useDeviceStatusSocket,
  useGetSuppressions,
  useGroupDetections,
  useIntl,
} from '../../../hooks';
import { buildDetectionGroupEvents } from '../../../services/detection-group-events';
import { Detection, Suppression } from '../../../types';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui';
import { DetectionEventRow } from './DetectionEventRow';
import { SuppressionEventRow } from './SuppressionEventRow';

const DEFAULT_EVENTS_PAGE_SIZE = 10;
const DETECTION_COLUMNS_COUNT = 7;
const SUPPRESSION_COLUMNS_COUNT = 2;

export type DetectionGroupEventsTableProps = {
  deviceId: string;
  detectionGroupId: string;
  groupCreatedAt: string;
  detectionCount?: number;
  suppressionCount?: number;
  setTotalDetections?: (total: number) => void;
  setTotalSuppressions?: (total: number) => void;
};

export function DetectionGroupEventsTable({
  deviceId,
  detectionGroupId,
  groupCreatedAt,
  detectionCount,
  suppressionCount,
  setTotalDetections,
  setTotalSuppressions,
}: DetectionGroupEventsTableProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [detectionsPageSize, setDetectionsPageSize] = useState(() =>
    Math.max(detectionCount ?? DEFAULT_EVENTS_PAGE_SIZE, DEFAULT_EVENTS_PAGE_SIZE),
  );

  useEffect(() => {
    setDetectionsPageSize(
      Math.max(detectionCount ?? DEFAULT_EVENTS_PAGE_SIZE, DEFAULT_EVENTS_PAGE_SIZE),
    );
  }, [detectionCount, detectionGroupId]);

  const {
    data: detectionsData,
    isLoading: detectionsLoading,
    error: detectionsError,
    refetch: refetchDetections,
  } = useGroupDetections(
    {
      deviceId,
      detectionGroupId,
      page: 1,
      pageSize: detectionsPageSize,
    },
    { enabled: Boolean(deviceId && detectionGroupId) },
  );

  useEffect(() => {
    if (detectionsData?.total && detectionsData.total > detectionsPageSize) {
      setDetectionsPageSize(detectionsData.total);
    }
  }, [detectionsData?.total, detectionsPageSize]);

  const {
    data: suppressionsData,
    isLoading: suppressionsLoading,
    error: suppressionsError,
    refetch: refetchSuppressions,
  } = useGetSuppressions({
    deviceId,
    detectionGroupId,
    detectionGroupCreatedAt: groupCreatedAt,
    enabled: Boolean(deviceId && detectionGroupId && groupCreatedAt),
  });

  const detections = useMemo(() => detectionsData?.detections ?? [], [detectionsData?.detections]);
  const suppressions = useMemo(
    () => suppressionsData?.suppressions ?? [],
    [suppressionsData?.suppressions],
  );
  const totalDetections = detectionsData?.total ?? detectionCount ?? 0;
  const totalColumns = Math.max(DETECTION_COLUMNS_COUNT, SUPPRESSION_COLUMNS_COUNT);
  const totalSuppressions = Math.max(suppressionCount ?? 0, suppressions.length);
  const suppressionColSpan = Math.max(totalColumns - 2, 1);
  const eventsTabLabel = intl.formatMessage({ id: 'detectionGroups.eventsTab' });
  const eventsAriaLabel = `${eventsTabLabel} (${totalDetections}/${totalSuppressions})`;

  useEffect(() => {
    setTotalDetections?.(totalDetections);
  }, [totalDetections, setTotalDetections]);

  useEffect(() => {
    setTotalSuppressions?.(suppressions.length);
  }, [suppressions.length, setTotalSuppressions]);

  const handleRetry = useCallback(() => {
    if (detectionsError) {
      refetchDetections();
    }
    if (suppressionsError) {
      refetchSuppressions();
    }
  }, [detectionsError, suppressionsError, refetchDetections, refetchSuppressions]);

  const handleNewSuppression = useCallback(
    ({ deviceId, suppression }: FrontendSuppressionEvent) => {
      if (!deviceId) return;

      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key)) return false;
          if (key[0] !== 'suppressions') return false;
          const params = (key[1] ?? {}) as { detectionGroupId?: string };
          return params.detectionGroupId === suppression.detectionGroupId;
        },
      });
    },
    [queryClient],
  );

  useDeviceStatusSocket(
    { onNewSuppression: handleNewSuppression },
    useMemo(() => [deviceId], [deviceId]),
  );

  const events = useMemo(
    () => buildDetectionGroupEvents(detections, suppressions),
    [detections, suppressions],
  );

  let tableContent: ReactNode;

  if (detectionsLoading || suppressionsLoading) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={totalColumns} className="py-3 text-sm text-center text-foreground">
          {intl.formatMessage({ id: 'detectionGroups.eventsLoading' })}
        </TableCell>
      </TableRow>
    );
  } else if (detectionsError || suppressionsError) {
    tableContent = (
      <TableRow>
        <TableCell
          colSpan={totalColumns}
          className="py-3 text-sm text-destructive flex items-center text-center gap-2"
        >
          {intl.formatMessage({ id: 'detectionGroups.eventsError' })}
          <Button variant="outline" size="sm" onClick={handleRetry} className="text-xs">
            {intl.formatMessage({ id: 'common.retry' })}
          </Button>
        </TableCell>
      </TableRow>
    );
  } else if (events.length === 0) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={totalColumns} className="py-3 text-sm text-center text-foreground">
          {intl.formatMessage({ id: 'detectionGroups.eventsEmpty' })}
        </TableCell>
      </TableRow>
    );
  } else {
    tableContent = events.map((event) =>
      event.type === 'detection' ? (
        <DetectionEventRow
          key={`detection-${event.record.id}`}
          detection={event.record as Detection}
        />
      ) : (
        <SuppressionEventRow
          key={`suppression-${event.record.id}`}
          suppression={event.record as Suppression}
          colSpan={suppressionColSpan}
        />
      ),
    );
  }

  return (
    <Table className="text-xs" aria-label={eventsAriaLabel}>
      <TableHeader>
        <TableRow>
          <TableHead>{intl.formatMessage({ id: 'detections.table.direction' })}</TableHead>
          <TableHead>{intl.formatMessage({ id: 'detections.table.bandwidth' })}</TableHead>
          <TableHead>{intl.formatMessage({ id: 'detections.table.offset' })}</TableHead>
          <TableHead>{intl.formatMessage({ id: 'detections.table.signalLevel' })}</TableHead>
          <TableHead>{intl.formatMessage({ id: 'detections.table.snr' })}</TableHead>
          <TableHead>{intl.formatMessage({ id: 'detections.table.pattern' })}</TableHead>
          <TableHead>{intl.formatMessage({ id: 'detections.table.timestamp' })}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{tableContent}</TableBody>
    </Table>
  );
}
